import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bot, Send, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = sessionStorage.getItem("dig_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem("dig_chat_session", id);
  }
  return id;
};

const WELCOME = { role: "assistant", content: "Hello! I'm the Dishan Assistant. Ask me anything about our consulting, education, wellness or corporate solutions, and I'll point you in the right direction." };

const QUICK_REPLIES = [
  "What services do you offer?",
  "How do I book a consultation?",
  "Tell me about internships",
  "What is the Dishan Leadership Program?",
];

export const ChatAssistant = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    await sendText(input.trim());
  };

  const sendText = async (text) => {
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.delta };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "I'm having a little trouble right now. Please email dishaninternationalgroup@gmail.com or WhatsApp +91 9163159890." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        className={`chat-launcher${open ? " hidden" : ""}`}
        onClick={() => setOpen(true)}
        aria-label="Open Dishan Assistant"
        data-testid="chat-launcher-button"
      >
        <Bot size={24} />
        <span>Ask Dishan</span>
      </button>
      {open && (
        <div className="chat-panel" data-testid="chat-panel">
          <div className="chat-head">
            <div className="chat-head-id">
              <span className="chat-avatar"><Bot size={18} /></span>
              <div>
                <strong>Dishan Assistant</strong>
                <small>Usually replies instantly</small>
              </div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close assistant" data-testid="chat-close-button"><X size={18} /></button>
          </div>
          <div className="chat-body" ref={bodyRef} data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={`msg-${i}-${m.role}`} className={`chat-msg ${m.role}`} data-testid={`chat-message-${m.role}`}>
                {m.content || <span className="chat-typing"><i /><i /><i /></span>}
              </div>
            ))}
            {messages.length === 1 && !busy && (
              <div className="quick-replies" data-testid="chat-quick-replies">
                {QUICK_REPLIES.map((q) => (
                  <button key={q} onClick={() => sendText(q)} data-testid={`quick-reply-${q.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{q}</button>
                ))}
              </div>
            )}
          </div>
          <div className="chat-cta">
            <Link to="/contact" onClick={() => setOpen(false)} data-testid="chat-consultation-link">Prefer a human? Book a consultation</Link>
          </div>
          <form className="chat-input" onSubmit={send}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about our services..."
              aria-label="Message the Dishan Assistant"
              data-testid="chat-input"
            />
            <button type="submit" disabled={busy || !input.trim()} aria-label="Send message" data-testid="chat-send-button"><Send size={16} /></button>
          </form>
        </div>
      )}
    </>
  );
};
